import { playerSearchWithMetaRatings } from "@/api/privateRequests/squad-builder/player-search-with-metaRatings";
import Button, { SearchButton } from "@/components/Button";
import { SignUpFormMobileClubImportModal } from "@/components/molecules/ActionModals/SignUpForMobileClubImportModal/SignUpForMobileClubImportModal";
import FiltersSidebar from "@/components/organisms/MyClubSidebars/Filters/FiltersSidebar";
import { binIcon, closingCross, filledFilterIcon } from "@/designSystemIcons";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import useSearch from "@/hooks/useSearch";
import {
  deselectPlayer,
  fetchPlayers,
  myPlayersSelector,
  removeSelectedPlayersFromClub,
  selectPlayer,
  updateFilters,
  updateIsSelecting,
  updateShowFilters,
} from "@/redux/myClub/myPlayersSlice";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { Filter } from "../../../components/organisms/MyClubSidebars/Filters/Filters.type";
import { useEscape } from "../../../hooks/useKeyboardShortcut";
import { SearchedPlayer } from "../../../redux/players/SearchedPlayer.type";
import { AppDispatch } from "../../../redux/store";
import { BREAKPOINTS } from "../../../settings";
import { AddPlayersDropdown } from "./components/AddPlayersDropdown";
import { PlayerTable } from "./components/PlayerTable";

export const MyClubPlayers = () => {
  const [chosenPlayer, setChosenPlayer] = useState<SearchedPlayer | null>(null);
  const {
    players,
    totalAmountOfPlayerPages,
    showFilters,
    filters,
    isSelecting,
    selectedPlayers,
    status,
  } = useSelector(myPlayersSelector);
  const dispatch = useDispatch<AppDispatch>();
  const [showMobileClubImportSignup, setShowMobileClubImportSignup] =
    useState(false);
  const [, setSearchParams] = useSearchParams();
  useEscape(() => setChosenPlayer(null));

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<"rating" | "price" | "profit">("rating");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    dispatch(fetchPlayers({ page, sortBy, sortDirection }));
  }, [dispatch, page]);

  useEffect(() => {
    setPage(1);
    dispatch(fetchPlayers({ page, sortBy, sortDirection }));
  }, [dispatch, sortBy, sortDirection]);

  const handleFilterClicked = (filter: Filter) => {
    setPage(1);
    dispatch(updateFilters(filter));
    dispatch(fetchPlayers({ page, sortBy, sortDirection }));
  };

  const handleSelectPlayer = (player: SearchedPlayer, isSelected: boolean) => {
    if (isSelected) {
      dispatch(deselectPlayer(player));
    } else {
      dispatch(selectPlayer(player));
    }
  };

  return chosenPlayer ? (
    <Outlet context={chosenPlayer} />
  ) : (
    <div>
      <div className="m-2 large-desktop:mx-8">
        <p className="pl-2 font-semibold text-sm mobile:text-base">
          My Players
        </p>
        <div className="flex flex-row w-full mx-auto justify-center max-w-content gap-x-2 mt-2">
          {showMobileClubImportSignup && (
            <SignUpFormMobileClubImportModal
              onDismiss={() => setShowMobileClubImportSignup(false)}
            />
          )}
          <PlayerTable
            fetchPlayersStatus={status}
            players={players}
            page={page}
            setPage={setPage}
            totalAmountOfPages={totalAmountOfPlayerPages}
            sortBy={sortBy}
            sortDirection={sortDirection}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            isSelecting={isSelecting}
            selectedPlayers={selectedPlayers}
            handleSelectPlayer={handleSelectPlayer}
          />
          {showFilters && (
            <FiltersSidebar
              onFilterClicked={handleFilterClicked}
              closeFilters={() => dispatch(updateShowFilters(false))}
              filters={filters}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const MyClubPlayersNavigationContext = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showFilters, isSelecting, selectedPlayers } =
    useSelector(myPlayersSelector);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const ref = useOutsideClick(() => {
    setSearchQuery("");
    setIsExpanded(false);
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery, , result] = useSearch(
    "",
    (name: string, page: number) =>
      playerSearchWithMetaRatings(name, page, "Playstation"),
  ) as [
    string,
    React.Dispatch<React.SetStateAction<string>>,
    boolean,
    SearchedPlayer[],
  ];

  return (
    <div
      className="flex flex-row justify-between w-full mobile:relative"
      ref={ref}
    >
      <>
        <Button
          label="Filters"
          variant="tertiary"
          isMobileFilterButton={isMobile}
          icon={<div className="w-4 h-4">{filledFilterIcon}</div>}
          onClick={() => {
            dispatch(updateShowFilters(!showFilters));
          }}
        />
        <div className="flex flex-row gap-x-2">
          {isSelecting && (
            <Button
              variant="tertiary"
              icon={<div className="fill-dark-secondary">{closingCross}</div>}
              onClick={() => {
                dispatch(updateIsSelecting(false));
              }}
            />
          )}
          <Button
            label={
              selectedPlayers.length > 0
                ? `Remove Players (${selectedPlayers.length})`
                : "Remove Players"
            }
            disabled={selectedPlayers.length === 0 && isSelecting}
            icon={
              !isSelecting && <div className="w-4 h-4 -mt-1">{binIcon}</div>
            }
            variant={
              selectedPlayers.length === 0 && isSelecting
                ? "disabled"
                : selectedPlayers.length > 0
                  ? "primary"
                  : "tertiary"
            }
            onClick={() => {
              if (selectedPlayers.length > 0) {
                dispatch(removeSelectedPlayersFromClub());
              } else {
                dispatch(updateIsSelecting(!isSelecting));
              }
            }}
          />

          <SearchButton
            isExpanded={isExpanded}
            label="Add Player"
            variant="tertiary"
            icon={<Plus className="h-5 w-5" />}
            onClick={(e) => {
              !isExpanded && setIsExpanded(true);
              e.stopPropagation();
            }}
            onChange={(searchQuery) => setSearchQuery(searchQuery)}
            searchQuery={searchQuery}
            onClose={() => {
              setSearchQuery("");
              setIsExpanded(false);
            }}
          />
        </div>
      </>
      {searchQuery && (
        <AddPlayersDropdown
          onClose={() => setSearchQuery("")}
          result={result}
        />
      )}
    </div>
  );
};
